// utils/permissions.js
// Centralized permission checking utilities

/**
 * Check if user is a public (unauthenticated) user
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user is public
 */
export const isPublicUser = (userRole) => {
  return userRole === 'Public' || !userRole;
};

/**
 * Check if user has instructor-level permissions
 * @param {string} userRole - The user's role ('Student', 'TA', 'Instructor', 'Admin', 'Public')
 * @returns {boolean} - True if user has instructor-level permissions
 */
export const hasInstructorPermissions = (userRole) => {
  if (isPublicUser(userRole)) return false;
  return userRole === 'Instructor' || userRole === 'TA' || userRole === 'Admin';
};

/**
 * Check if user can edit requirements
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can edit requirements
 */
export const canEditRequirements = (userRole) => {
  if (isPublicUser(userRole)) return false;
  return hasInstructorPermissions(userRole);
};

/**
 * Check if user can view all feedback
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can view all feedback
 */
export const canViewAllFeedback = (userRole) => {
  return hasInstructorPermissions(userRole);
};

/**
 * Check if user can manage quotas
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can manage quotas
 */
export const canManageQuotas = (userRole) => {
  return hasInstructorPermissions(userRole);
};

/**
 * Check if user can view user information
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can view user info
 */
export const canViewUserInfo = (userRole) => {
  return hasInstructorPermissions(userRole);
};

/**
 * Check if user can view consents
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can view consents
 */
export const canViewConsents = (userRole) => {
  return hasInstructorPermissions(userRole);
};

/**
 * Check if user can perform evaluations
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can perform evaluations
 */
export const canEvaluate = (userRole) => {
  return hasInstructorPermissions(userRole);
};

/**
 * Check if user can access detailed requirements with rubrics
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can access detailed requirements
 */
export const canAccessDetailedRequirements = (userRole) => {
  return hasInstructorPermissions(userRole);
};

/**
 * Get the user's permission level
 * @param {string} userRole - The user's role
 * @returns {string} - Permission level ('admin', 'student')
 */
export const getPermissionLevel = (userRole) => {
  if (hasInstructorPermissions(userRole)) {
    return 'admin';
  }
  return 'student';
};

/**
 * Check if user can select other users for viewing feedback
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can select other users
 */
export const canSelectOtherUsers = (userRole) => {
  return hasInstructorPermissions(userRole);
};

/**
 * Check if user can manage classes (create, archive, edit)
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can manage classes
 */
export const canManageClasses = (userRole) => {
  return userRole === 'Admin';
};

/**
 * Check if user can enroll students in classes
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can enroll students
 */
export const canEnrollStudents = (userRole) => {
  if (isPublicUser(userRole)) return false;
  return userRole === 'Admin' || userRole === 'Instructor';
};

/**
 * Check if user has API access (public users don't)
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can use API features
 */
export const hasApiAccess = (userRole) => {
  return !isPublicUser(userRole);
};

/**
 * Check if user can save/edit content (public users can't)
 * @param {string} userRole - The user's role
 * @returns {boolean} - True if user can save/edit
 */
export const canSaveContent = (userRole) => {
  return !isPublicUser(userRole);
};

/**
 * Get role display name with TA clarification
 * @param {string} userRole - The user's role
 * @returns {string} - Display-friendly role name
 */
export const getRoleDisplayName = (userRole) => {
  switch (userRole) {
    case 'TA':
      return 'Teaching Assistant';
    case 'Instructor':
      return 'Instructor';
    case 'Student':
      return 'Student';
    case 'Admin':
      return 'Administrator';
    case 'Public':
      return 'Public Viewer';
    default:
      return userRole || 'Unknown';
  }
};

/**
 * Get role-based UI colors
 * @param {string} userRole - The user's role
 * @returns {object} - Object containing color classes
 */
export const getRoleColors = (userRole) => {
  if (userRole === 'Instructor') {
    return {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-200',
      accent: 'text-purple-600'
    };
  } else if (userRole === 'TA') {
    return {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-200',
      accent: 'text-green-600'
    };
  } else if (userRole === 'Admin') {
    return {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      accent: 'text-red-600'
    };
  } else {
    return {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      accent: 'text-blue-600'
    };
  }
};